import type { DragEvent, KeyboardEvent } from 'react'
import { useState } from 'react'

/** The keys that move an item one place, named for `aria-keyshortcuts`. */
export const REORDER_KEYS = 'Alt+ArrowUp Alt+ArrowDown'

/**
 * Where an item dragged from `from` lands when dropped on the item at `at`:
 * before it in that item's upper half, after it in the lower. The index is in
 * the list as it stands once the dragged item has left its own place.
 */
export function dropIndex(
  from: number,
  at: number,
  lowerHalf: boolean,
  count: number,
) {
  let to = at + (lowerHalf ? 1 : 0)
  if (from < to) to -= 1
  return Math.min(count - 1, to)
}

/**
 * One way to reorder a list in place: drag an item onto another, or press
 * Alt+Arrow on a focused item to move it one place. The keyboard path is the
 * same move as the drag, so nothing a pointer can do is out of reach.
 *
 * `target` goes on the element a drop lands on; `handle` on the focusable
 * element that is dragged and takes the keys.
 *
 * A drag holds the item's id rather than its place, and finds the place again
 * at the drop: the list may be redrawn in another order while the pointer is
 * down, and the item moved must be the one that was picked up.
 */
export function useReorder({
  ids,
  enabled,
  onMove,
}: {
  ids: readonly string[]
  enabled: boolean
  onMove: (from: number, to: number) => void
}) {
  const [dragging, setDragging] = useState<string | null>(null)
  const count = ids.length
  const target = (at: number) => ({
    onDragOver: (event: DragEvent) => {
      if (!enabled || dragging === null) return
      event.preventDefault()
    },
    onDrop: (event: DragEvent<HTMLElement>) => {
      if (!enabled || dragging === null) return
      event.preventDefault()
      setDragging(null)
      const from = ids.indexOf(dragging)
      if (from < 0) return
      const box = event.currentTarget.getBoundingClientRect()
      const lowerHalf = event.clientY >= box.top + box.height / 2
      onMove(from, dropIndex(from, at, lowerHalf, count))
    },
  })
  const handle = (at: number, id: string) => ({
    draggable: enabled,
    'aria-keyshortcuts': enabled ? REORDER_KEYS : undefined,
    onDragStart: (event: DragEvent) => {
      setDragging(id)
      event.dataTransfer.effectAllowed = 'move'
      event.dataTransfer.setData('text/plain', id)
    },
    onDragEnd: () => setDragging(null),
    onKeyDown: (event: KeyboardEvent) => {
      if (!enabled || !event.altKey) return
      if (event.key === 'ArrowUp' && at > 0) {
        event.preventDefault()
        onMove(at, at - 1)
      } else if (event.key === 'ArrowDown' && at < count - 1) {
        event.preventDefault()
        onMove(at, at + 1)
      }
    },
  })
  return { target, handle }
}
